import AxiosProvider from '@/app/utils/api/axios-provider';
import { AxiosResponse } from 'axios';
import { Datasource } from '@/app/shared/types/datasource.interface';
import { Client } from '@/app/shared/types/models/client.interface';
import { MultiDatasourceRequest } from '@/app/shared/types/multi-datasource-request.interface';
import { useClientStore } from '@/app/features/ams/client/client-store';

/**
 * Admin API calls for multi (virtual) datasources. Creation and membership live in
 * admin because they change DATA_SOURCES / DATA_SOURCE_LINKS / CLIENTS_DATA_SOURCES;
 * AMS only reads them (segment dropdown, field tree, Validate, deploy).
 */
export default {
  createMultiDatasource(
    request: MultiDatasourceRequest
  ): Promise<AxiosResponse<{ data: Datasource }>> {
    return AxiosProvider.adminApiClient.post('/datasources/multi', request);
  },

  updateMultiDatasource(
    id: number,
    request: MultiDatasourceRequest
  ): Promise<AxiosResponse<{ data: Datasource }>> {
    return AxiosProvider.adminApiClient.patch(
      `/datasources/multi/${id}`,
      request
    );
  },

  getMultiDatasourceMembers(
    id: number
  ): Promise<AxiosResponse<{ data: Datasource[] }>> {
    return AxiosProvider.adminApiClient.get(`/datasources/multi/${id}/members`);
  },

  getMultiDatasourceUsage(
    id: number
  ): Promise<AxiosResponse<{ data: { versionControls: number } }>> {
    return AxiosProvider.adminApiClient.get(`/datasources/multi/${id}/usage`);
  },

  deleteMultiDatasource(id: number): Promise<AxiosResponse> {
    return AxiosProvider.adminApiClient.delete(`/datasources/multi/${id}`);
  },

  /** Every datasource known to admin; the multi ones are the candidates for Attach. */
  getAllDatasources(): Promise<AxiosResponse<{ data: Datasource[] }>> {
    return AxiosProvider.adminApiClient.get('/datasources');
  },

  /** Attach exactly these multi datasources to the client; the others are detached. */
  setClientMultiDatasources(
    clientId: number,
    multiDataSourceIds: Array<number>
  ): Promise<AxiosResponse<{ data: Datasource[] }>> {
    return AxiosProvider.adminApiClient.put(
      `/datasources/multi/by-client/${clientId}`,
      { multiDataSourceIds }
    );
  },
};

/**
 * Re-read the clients from AMS and re-select the current one, so a new or changed multi
 * datasource shows up in the list and in the coding window's datasource list.
 */
export const refreshSelectedClient = async (): Promise<void> => {
  const clientStore = useClientStore();
  const selectedId = clientStore.selectedClient?.id;
  await clientStore.fetchClientData();
  const refreshed = clientStore.clientList.find(
    (client: Client) => client.id === selectedId
  );
  if (refreshed) {
    await clientStore.setSelectedClient(refreshed);
  }
};
